import { formatTime, getGradeLabel, getStudentName } from '@car-pool/shared/schoolRecords';
import { getStudentPickupStepIndex, STUDENT_PICKUP_STEPS } from '@car-pool/shared/studentDetails';
import SchoolAvatar from './SchoolAvatar';
import SchoolInfoRow from './SchoolInfoRow';

function formatDismissalTime(value) {
  if (!value) return 'Not scheduled';

  const [hours = '0', minutes = '0'] = String(value).split(':');
  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatSchoolName(school) {
  return school?.school_nickname || school?.legal_name || 'School';
}

function formatSchoolAddress(school) {
  if (!school) return 'School address unavailable';

  const cityLine = [school.city, school.state].filter(Boolean).join(', ');
  return [
    school.street_address,
    [cityLine, school.zip_code].filter(Boolean).join(' '),
  ].filter(Boolean).join('\n') || 'School address unavailable';
}

function getStatusChipLabel(status) {
  switch (String(status || 'waiting').toLowerCase()) {
    case 'called':
    case 'on_the_way':
      return 'On the way';
    case 'arrived':
    case 'ready':
    case 'in_zone':
    case 'driver_in_zone':
      return 'In pickup zone';
    case 'released':
      return 'Released';
    case 'sent':
      return 'Sent to carpool';
    case 'picked_up':
    case 'completed':
      return 'Picked up';
    case 'waiting':
    default:
      return 'Waiting';
  }
}

function getDirectionsUrl(school) {
  const address = [
    school?.street_address,
    school?.city,
    school?.state,
    school?.zip_code,
  ].filter(Boolean).join(', ');

  return address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : '';
}

export default function StudentProfileCard({
  student,
  school,
  dismissalStatus,
  gradeLabelMap = {},
  gradeSplit = null,
  dismissalTime,
  noSchool = false,
  carpoolLine,
  authorizedDrivers = [],
  loadingDetails = false,
  detailsError = '',
  onEdit,
  onInvite,
  onAssignDriver,
  className = '',
}) {
  if (!student) return null;

  const fullName = getStudentName(student);
  const gradeLabel = getGradeLabel(student.grade, gradeLabelMap);
  const gradeDisplay = gradeSplit?.split_code ? `${gradeLabel} - ${gradeSplit.split_code}` : gradeLabel;
  const currentStep = getStudentPickupStepIndex(dismissalStatus?.status);
  const updatedAt = dismissalStatus?.picked_at || dismissalStatus?.sent_at || dismissalStatus?.called_at || dismissalStatus?.updated_at || dismissalStatus?.created_at;
  const effectiveDismissalTime = noSchool ? null : (carpoolLine?.dismissal_time || dismissalTime);
  const schoolTimeOptions = { timeZone: school?.time_zone, isDst: Boolean(Number(school?.is_dst)) };
  const directionsUrl = getDirectionsUrl(school);
  const classes = ['sas-student-profile-card', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <div className="sas-profile-hero">
        <div className="sas-profile-hero-actions">
          {onAssignDriver && (
            <button type="button" className="sa-btn sa-btn-secondary sas-profile-edit-btn" onClick={onAssignDriver}>
              Assign Driver
            </button>
          )}
          {onInvite && (
            <button type="button" className="sa-btn sa-btn-secondary sas-profile-edit-btn" onClick={onInvite}>
              Invite Driver
            </button>
          )}
          <button type="button" className="sa-btn sa-btn-secondary sas-profile-edit-btn" onClick={onEdit}>
            Edit
          </button>
        </div>
        <SchoolAvatar
          name={fullName}
          seed={student.id}
          photoPath={student.photo_relative_path}
          enableViewer
          large
          className="sas-profile-hero-avatar"
        />
        <h3>{fullName} ({gradeDisplay})</h3>
        <span>{formatSchoolName(school)}</span>
      </div>

      <div className="sap-section-card sas-profile-section">
        <div className="sas-profile-section-head">
          <div className="sap-section-caption">Pickup Status</div>
          <div className="sas-profile-section-meta">
            <span className="sas-profile-status-chip">{getStatusChipLabel(dismissalStatus?.status)}</span>
            <span className="sas-profile-section-updated">
              {updatedAt ? `updated ${formatTime(updatedAt, schoolTimeOptions)}` : 'awaiting update'}
            </span>
          </div>
        </div>

        <div className="sas-profile-step-list">
          {STUDENT_PICKUP_STEPS.map((step, index) => {
            const completed = index <= currentStep;
            return (
              <div key={step.key} className={`sas-profile-step ${completed ? 'done' : ''}`}>
                <span className={`sas-profile-step-icon ${completed ? 'done' : ''}`} aria-hidden="true">
                  {completed ? '✓' : ''}
                </span>
                <span className="sas-profile-step-label">{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="sap-section-card sas-profile-section">
        <div className="sap-section-caption">Pickup Details</div>
        {detailsError ? (
          <div className="sap-section-empty">{detailsError}</div>
        ) : (
          <>
            <SchoolInfoRow label="Pickup time" value={noSchool ? 'No school' : formatDismissalTime(effectiveDismissalTime)} />
            <SchoolInfoRow label="Pickup zone" value={loadingDetails ? 'Loading…' : carpoolLine?.name || 'Not assigned'} />
            <SchoolInfoRow label="Pickup address" value={formatSchoolAddress(school)} />
            {directionsUrl ? (
              <a className="sas-profile-directions-btn" href={directionsUrl} target="_blank" rel="noreferrer">
                Get Directions
              </a>
            ) : null}
          </>
        )}
      </div>

      <div className="sap-section-card sas-profile-section">
        <div className="sap-section-caption">Parents &amp; Authorized Drivers</div>
        <div className="sas-profile-drivers-caption">Any of the following parents or authorized drivers may pick up this student</div>
        {loadingDetails ? (
          <div className="sap-section-empty">Loading parents and authorized drivers…</div>
        ) : authorizedDrivers.length === 0 ? (
          <div className="sap-section-empty">No parents or authorized drivers on record.</div>
        ) : (
          <div className="sas-profile-driver-list">
            {authorizedDrivers.map((driver, index) => (
              <div key={driver.id || `${driver.name}-${index}`} className="sas-profile-driver-row">
                <SchoolAvatar name={driver.name} seed={index + 1} className="sas-profile-driver-avatar" />
                <div className="sas-profile-driver-copy">
                  <div className="sap-student-name">{driver.name}</div>
                  <div className="sap-student-grade">{driver.relation}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
